/**
 * When a press on a modal's backdrop is a dismissal, and when it is not.
 *
 * A native `<dialog>` paints its backdrop as a pseudo-element of itself, so a
 * click on the backdrop targets the dialog while a click on the content targets
 * a child. Testing `event.target === dialog` in a `click` handler therefore
 * looks like it identifies backdrop clicks. It does not.
 *
 * **`click` fires on the common ancestor of where the pointer went down and
 * where it came up.** Press on a line of text inside the modal, drag out over
 * the backdrop to finish selecting it, release — the common ancestor of the
 * text node and the backdrop is the `<dialog>` itself, so the handler sees
 * exactly what a real backdrop click looks like and throws away whatever was in
 * the modal. That is the single most-reported frustration from the counter, and
 * it costs the operator whatever they had typed.
 *
 * The rule is therefore: a dismissal is a press **and** a release on the
 * backdrop. Either end landing on the content means the gesture was about the
 * content, whichever direction it went.
 *
 * Kept here as a pure module rather than inline in `ui.tsx` because `apps/pos`
 * has no DOM testing library by choice — logic that matters is extracted and
 * asserted, the same way `wizard-machine.ts` holds the import step budget.
 */

export interface BackdropGesture {
  /** Did the pointer go down on the backdrop rather than on the modal? */
  readonly pressedBackdrop: boolean;
  /** Did it come up there too? */
  readonly releasedBackdrop: boolean;
}

/**
 * No gesture in progress.
 *
 * Also what a gesture is reset to when a modal opens: a stale flag left by the
 * previous one would let the first click anywhere dismiss the new modal.
 */
export const NO_GESTURE: BackdropGesture = {
  pressedBackdrop: false,
  releasedBackdrop: false,
};

/** Pointer down. Starts a fresh gesture — the previous one is over either way. */
export function press(onBackdrop: boolean): BackdropGesture {
  return { pressedBackdrop: onBackdrop, releasedBackdrop: false };
}

export function release(gesture: BackdropGesture, onBackdrop: boolean): BackdropGesture {
  return { ...gesture, releasedBackdrop: onBackdrop };
}

/**
 * Should this click close the modal?
 *
 * `clickOnBackdrop` is the `event.target === dialog` test, kept as a third
 * condition rather than dropped: it costs nothing and it is the one that holds
 * if a click ever arrives without the pointer events that should precede it.
 */
export function dismisses(gesture: BackdropGesture, clickOnBackdrop: boolean): boolean {
  return gesture.pressedBackdrop && gesture.releasedBackdrop && clickOnBackdrop;
}
